<template>
  <ProjectNodeLayout disable-container>
    <template #content>
      <LayoutOrder v-if="node != null">
        <template #main>
          <ImageSection
            class="tw-max-w-screen-xl"
            pre-title="Step 1"
            title="Select an image"
            v-model="reinstall.image"
            :installed-image="node.image"
            :current-flavour="node.flavour"
            :cloud-provider-type="node.cloudProvider ? node.cloudProvider.type : null"
            :show-installed="true"
            :force-preselect="true"
          />
          <GpFadeTransitionGroup>
            <template v-if="reinstall.image != null">
              <LoginMedthodSection
                class="tw-max-w-screen-xl"
                pre-title="Step 2"
                title="Select a login method"
                v-model="reinstall.authentication"
                :current-image="reinstall.image"
              />
              <SettingsSection
                class="tw-max-w-screen-xl"
                v-model="reinstall.settings"
                pre-title="Step 3"
                title="Settings"
              />
            </template>
          </GpFadeTransitionGroup>
        </template>
        <template #summary>
          <ReinstallSummary
            class="tw-mt-8"
            :node="node"
            :reinstall="reinstall"
            :sections-mapping="sectionsMapping"
          />
        </template>
      </LayoutOrder>

      <GpBrandMessage v-else class="mx-auto" message="Something went wrong." />
    </template>
  </ProjectNodeLayout>
</template>

<script lang="ts">
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { GpBrandMessage, GpFadeTransitionGroup, useFaIcons } from '@gportal/uikit';
import ImageSection from '@panel/components/node/create/image/ImageSection.vue';
import LoginMedthodSection from '@panel/components/node/create/login-method/LoginMedthodSection.vue';
import SettingsSection from '@panel/components/node/create/settings/SettingsSection.vue';
import visibleSectionsSymbol from '@panel/components/node/create/symbols';
import type { OrderSections } from '@panel/components/node/create/types';
import ReinstallSummary from '@panel/components/project/reinstall/ReinstallSummary.vue';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import ProjectNodeLayout from '@panel/layouts/Layout.Project.Node.vue';
import LayoutOrder from '@panel/layouts/order/Layout.Order.vue';
import { type ReinstallNode, useNodeStore } from 'gpcore-apikit/store/pinia/node';
import type { BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import type { Node } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import { type ComputedRef, computed, defineComponent, provide, reactive, ref, watch } from 'vue';

export default defineComponent({
  components: {
    ProjectNodeLayout,
    ImageSection,
    LoginMedthodSection,
    SettingsSection,
    ReinstallSummary,
    LayoutOrder,
    GpBrandMessage,
    GpFadeTransitionGroup,
  },
  setup() {
    useProjectViewTitle('Reinstall node');
    useFaIcons({ faArrowLeft });

    const imageType = ref<'public' | 'project'>('public');
    const reinstall = reactive<ReinstallNode>({
      image: null,
      id: null,
      fqdn: '',
      settings: {
        userData: '',
      },
      authentication: null,
    });

    const nodeStore = useNodeStore();
    const node = computed(() => nodeStore.node);
    watch(
      () => nodeStore.node,
      () => {
        if (nodeStore.node != null) {
          reinstall.id = nodeStore.node.id;
          reinstall.fqdn = nodeStore.node.fqdn;
          reinstall.image = nodeStore.node.image as unknown as BasicImage;
          reinstall.authentication = null;
        }
      },
    );

    const sectionsMapping = reactive<OrderSections>({
      image: { label: 'Image', isMounted: false, isVisible: false },
      login: { label: 'Login method', isMounted: false, isVisible: false },
      settings: { label: 'Settings', isMounted: false, isVisible: false },
    });
    provide(visibleSectionsSymbol, sectionsMapping);

    return {
      imageType,
      node: node as ComputedRef<Node>,
      reinstall: reinstall as ReinstallNode,
      sectionsMapping,
    };
  },
});
</script>
